import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import * as clientesApi from '@/api/clientes.api';
import * as installationApi from '@/api/installation.api';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { jobStatusBadge, encuestaBadge } from '@/lib/badges';
import { formatPhoneNumber, dateTime } from '@/lib/format';
import { money } from '@/lib/money';
import { cn } from '@/lib/cn';

/**
 * CLIENTES — la cartera del negocio: quién compró, qué, cuándo y por cuánto.
 * Solo lectura (la agregación vive en el API); el click abre la ficha completa
 * del cliente con su historial y el link a su conversación.
 */

type ComproFiltro = 'todos' | 'si' | 'no';

const COMPRO_CHIPS: { value: ComproFiltro; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'si', label: 'Compraron' },
  { value: 'no', label: 'No compraron' },
];

export default function Clientes() {
  const [search, setSearch] = useState('');
  const [compro, setCompro] = useState<ComproFiltro>('todos');
  const [zona, setZona] = useState('');
  const [esteMes, setEsteMes] = useState(false);

  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
  });
  const currencyPrefix = crmView?.currencyPrefix || 'RD$';

  const { data, isLoading } = useQuery({
    queryKey: ['clientes', 'list', { search, compro, zona, esteMes }],
    queryFn: () =>
      clientesApi.listClientes({
        search: search.trim() || undefined,
        compro: compro === 'todos' ? undefined : compro,
        zona: zona || undefined,
        esteMes: esteMes ? 'true' : undefined,
        take: 100,
      }),
  });

  const zonaOptions = [
    { value: '', label: 'Zona: todas' },
    ...(crmView?.crm.zonas ?? []).map((z) => ({ value: z, label: z })),
  ];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Clientes"
        description="Tu cartera: quién te compró, qué se llevó y cuánto ha dejado cada uno."
      />

      {/* Buscador + filtros simples */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o teléfono…"
            className="h-10 w-full rounded-md border border-surface-border bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
        </div>
        <div className="flex items-center gap-1 rounded-md border border-surface-border bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
          {COMPRO_CHIPS.map((chip) => (
            <button
              key={chip.value}
              onClick={() => setCompro(chip.value)}
              className={cn(
                'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                compro === chip.value
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div className="w-44">
          <Select options={zonaOptions} value={zona} onChange={(e) => setZona(e.target.value)} />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={esteMes}
            onChange={(e) => setEsteMes(e.target.checked)}
            className="h-4 w-4 rounded border-surface-border text-brand-600 focus:ring-brand-500"
          />
          Compraron este mes
        </label>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : !data?.items.length ? (
        <EmptyState
          icon={Users}
          title="Sin clientes con ese filtro"
          description="Prueba con otro filtro o borra la búsqueda."
        />
      ) : (
        <Card>
          <div className="flex items-center justify-between border-b border-surface-border p-5 dark:border-slate-800">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {data.total} cliente{data.total === 1 ? '' : 's'}
            </h2>
          </div>
          <CardBody className="p-0">
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {data.items.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/clientes/${c.id}`}
                    className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 sm:flex-nowrap"
                  >
                    <Avatar name={c.nombre} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                        {c.nombre}
                      </p>
                      <p className="truncate text-xs text-slate-400">
                        {[formatPhoneNumber(c.telefono), c.zona].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <div className="min-w-0 flex-1 text-sm">
                      {c.ultimaCompra ? (
                        <>
                          <p className="truncate text-slate-700 dark:text-slate-200">
                            {c.ultimaCompra.modelo ?? 'Compra sin modelo'} ·{' '}
                            {money(c.ultimaCompra.precio, currencyPrefix)}
                          </p>
                          <p className="truncate text-xs text-slate-400">
                            {dateTime(c.ultimaCompra.fecha)}
                            {c.tecnico ? ` · Técnico: ${c.tecnico}` : ''}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-slate-400">Sin compras todavía</p>
                      )}
                    </div>
                    <div className="w-28 shrink-0 text-right">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {money(c.totalHistorico, currencyPrefix)}
                      </p>
                      <p className="text-xs text-slate-400">
                        {c.comprasCount} compra{c.comprasCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {c.estadoUltimoTrabajo && (
                        <Badge variant={jobStatusBadge(c.estadoUltimoTrabajo).variant}>
                          {jobStatusBadge(c.estadoUltimoTrabajo).label}
                        </Badge>
                      )}
                      {c.encuesta && (
                        <Badge variant={encuestaBadge(c.encuesta).variant}>
                          {encuestaBadge(c.encuesta).label}
                        </Badge>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
